import React from 'react'
import { useLocation, Link } from 'react-router-dom'
import styled from 'styled-components'
import { Home as HomeIconFeather } from 'react-feather'
import { useTranslation } from 'react-i18next'

const HomeIcon = styled(HomeIconFeather)`
  width: 20px;
  height: 20px;
`

// Styled components for sidebar
const SidebarContainer = styled.div`
  width: 256px;
  background-color: #000;
  padding: 1.5rem;
  position: fixed;
  top: 0;
  left: 0;
  height: 100%;
  z-index: 1000;

  @media (max-width: 1440px) {
    width: 220px;
  }

  @media (max-width: 768px) {
    display: none;
  }
`

const MobileSidebarContainer = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;
    padding: 1rem;
    padding-bottom: 4rem;
    background-color: #212121;
    width: 100%;
    position: relative;
  }
`

const NavContainer = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const NavLink = styled(Link)<{ active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  text-align: left;
  padding: 0.75rem;
  background-color: ${({ active }) => (active ? '#333333' : 'transparent')};
  border: none;
  border-radius: 0.5rem;
  color: ${({ active }) => (active ? '#e7ac08' : '#ffffff')};
  font-size: 1rem;
  font-weight: ${({ active }) => (active ? '600' : '400')};
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;

  &:hover {
    background-color: ${({ active }) => (active ? '#333333' : '#2a2a2a')};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`

const ExternalNavLink = styled.a<{ active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  text-align: left;
  padding: 0.75rem;
  background-color: ${({ active }) => (active ? '#333333' : 'transparent')};
  border: none;
  border-radius: 0.5rem;
  color: ${({ active }) => (active ? '#e7ac08' : '#ffffff')};
  font-size: 1rem;
  font-weight: ${({ active }) => (active ? '600' : '400')};
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;

  &:hover {
    background-color: ${({ active }) => (active ? '#333333' : '#2a2a2a')};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`

const MobileHeaderRow = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }
`

const MobileNavContainer = styled.nav<{ open?: boolean }>`
  display: none;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
  background-color: #212121;
  position: fixed;
  top: 4.5rem;
  left: 0;
  height: calc(100% - 4.5rem);
  z-index: 1000;
  padding: 0 1rem;

  @media (max-width: 768px) {
    display: ${({ open }) => (open ? 'flex' : 'none')};
  }
`

const MobileNavLink = styled(Link)<{ active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  text-align: center;
  justify-content: center;
  padding: 0.75rem 1rem;
  background-color: ${({ active }) => (active ? '#333333' : 'transparent')};
  border: 1px solid ${({ active }) => (active ? '#e7ac08' : '#444444')};
  border-radius: 0.5rem;
  color: ${({ active }) => (active ? '#e7ac08' : '#ffffff')};
  font-size: 1rem;
  font-weight: ${({ active }) => (active ? '600' : '400')};
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    background-color: ${({ active }) => (active ? '#333333' : '#2a2a2a')};
    border-color: ${({ active }) => (active ? '#e7ac08' : '#666666')};
  }

  svg {
    width: 20px;
    height: 20px;
  }

  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding: 0.625rem 0.75rem;
    gap: 0.375rem;
  }
`

const MobileExternalNavLink = styled.a<{ active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  text-align: center;
  justify-content: center;
  padding: 0.75rem 1rem;
  background-color: ${({ active }) => (active ? '#333333' : 'transparent')};
  border: 1px solid ${({ active }) => (active ? '#e7ac08' : '#444444')};
  border-radius: 0.5rem;
  color: ${({ active }) => (active ? '#e7ac08' : '#ffffff')};
  font-size: 1rem;
  font-weight: ${({ active }) => (active ? '600' : '400')};
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    background-color: ${({ active }) => (active ? '#333333' : '#2a2a2a')};
    border-color: ${({ active }) => (active ? '#e7ac08' : '#666666')};
  }

  svg {
    width: 20px;
    height: 20px;
  }

  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding: 0.625rem 0.75rem;
    gap: 0.375rem;
  }
`

const MobileMenuButton = styled.button`
  display: none;

  @media (max-width: 768px) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 0.5rem;
    border: 1px solid #444444;
    background-color: transparent;
    color: #ffffff;
    cursor: pointer;

    &:hover {
      background-color: #2a2a2a;
      border-color: #666666;
    }

    svg {
      width: 22px;
      height: 22px;
    }
  }
`

const Logo = styled.img`
  width: 100%;
  height: auto;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    width: 60%;
    margin-bottom: 0.5rem;
  }
`

export default function Sidebar() {
  const { t } = useTranslation()
  const location = useLocation()
  const [mobileOpen, setMobileOpen] = React.useState(false)

  // 判断当前路由是否应该高亮Pool菜单项
  const isPoolActive =
    location.pathname === '/pool' ||
    location.pathname.startsWith('/add') ||
    location.pathname.startsWith('/remove') ||
    location.pathname.startsWith('/create') ||
    location.pathname.startsWith('/find')

  // 判断当前路由是否应该高亮Swap菜单项
  const isSwapActive = location.pathname === '/swap' || location.pathname === '/'

  // 判断新菜单项是否激活
  const isLotteryActive = location.pathname === '/lottery'
  // const isBridgeActive = location.pathname === '/bridge'
  const isLaunchpadActive = location.pathname === '/launchpad'
  const isMemeLauncherActive = location.pathname === '/meme-launcher'
  const isYieldFarmingActive = location.pathname === '/yield-farming'
  const isNodeMiningActive = location.pathname === '/node-mining'
  const isGenesisEagleActive = location.pathname === '/genesis-eagle'

  return (
    <>
      <MobileSidebarContainer>
        <MobileHeaderRow>
          <Logo src={'/images/logo.png'} alt="USD1 Swap" />
          <MobileMenuButton aria-label="Toggle menu" aria-expanded={mobileOpen} onClick={() => setMobileOpen(v => !v)}>
            {mobileOpen ? (
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </MobileMenuButton>
        </MobileHeaderRow>
        <MobileNavContainer open={mobileOpen}>
          <MobileExternalNavLink
            href="https://usd1swap.ai"
            rel="noopener noreferrer"
            onClick={() => setMobileOpen(false)}
          >
            <HomeIcon />
            <span>{t('home')}</span>
          </MobileExternalNavLink>
          <MobileNavLink to="/" active={isSwapActive} onClick={() => setMobileOpen(false)}>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
              />
            </svg>
            <span>{t('swap')}</span>
          </MobileNavLink>
          <MobileNavLink to="/pool" active={isPoolActive} onClick={() => setMobileOpen(false)}>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
              />
            </svg>
            <span>{t('pool')}</span>
          </MobileNavLink>
          <MobileExternalNavLink
            href="https://bridge.movachain.com"
            rel="noopener noreferrer"
            target="_blank"
            onClick={() => setMobileOpen(false)}
          >
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
              />
            </svg>
            <span>{t('bridge')}</span>
          </MobileExternalNavLink>
          <MobileNavLink to="/lottery" active={isLotteryActive} onClick={() => setMobileOpen(false)}>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
              />
            </svg>
            <span>{t('lottery')}</span>
          </MobileNavLink>
          <MobileNavLink to="/launchpad" active={isLaunchpadActive} onClick={() => setMobileOpen(false)}>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <span>{t('launchpad')}</span>
          </MobileNavLink>
          <MobileNavLink to="/meme-launcher" active={isMemeLauncherActive} onClick={() => setMobileOpen(false)}>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h1m4 0h1m6-6a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>{t('memeLauncher')}</span>
          </MobileNavLink>
          <MobileNavLink to="/yield-farming" active={isYieldFarmingActive} onClick={() => setMobileOpen(false)}>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
              />
            </svg>
            <span>{t('yieldFarmingMenu')}</span>
          </MobileNavLink>
          <MobileNavLink to="/node-mining" active={isNodeMiningActive} onClick={() => setMobileOpen(false)}>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
              />
            </svg>
            <span>{t('nodeMining')}</span>
          </MobileNavLink>
          <MobileNavLink to="/genesis-eagle" active={isGenesisEagleActive} onClick={() => setMobileOpen(false)}>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
              />
            </svg>
            <span>{t('genesisEagle')}</span>
          </MobileNavLink>
        </MobileNavContainer>
      </MobileSidebarContainer>
      <SidebarContainer>
        <Logo src={'/images/logo.png'} alt="USD1 Swap" />
        <NavContainer>
          <ExternalNavLink href="https://usd1swap.ai" rel="noopener noreferrer">
            <HomeIcon />
            <span>{t('home')}</span>
          </ExternalNavLink>
          <NavLink to="/" active={isSwapActive}>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
              />
            </svg>
            <span>{t('swap')}</span>
          </NavLink>
          <NavLink to="/pool" active={isPoolActive}>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
              />
            </svg>
            <span>{t('pool')}</span>
          </NavLink>
          <ExternalNavLink href="https://bridge.movachain.com" rel="noopener noreferrer" target="_blank">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
              />
            </svg>
            <span>{t('bridge')}</span>
          </ExternalNavLink>
          {/* <NavLink to="/bridge" active={isBridgeActive}>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
              />
            </svg>
            <span>{t('bridge')}</span>
          </NavLink> */}
          <NavLink to="/lottery" active={isLotteryActive}>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
              />
            </svg>
            <span>{t('lottery')}</span>
          </NavLink>
          <NavLink to="/launchpad" active={isLaunchpadActive}>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <span>{t('launchpad')}</span>
          </NavLink>
          <NavLink to="/meme-launcher" active={isMemeLauncherActive}>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h1m4 0h1m6-6a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>{t('memeLauncher')}</span>
          </NavLink>
          <NavLink to="/yield-farming" active={isYieldFarmingActive}>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
              />
            </svg>
            <span>{t('yieldFarmingMenu')}</span>
          </NavLink>
          <NavLink to="/node-mining" active={isNodeMiningActive}>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
              />
            </svg>
            <span>{t('nodeMining')}</span>
          </NavLink>
          <NavLink to="/genesis-eagle" active={isGenesisEagleActive}>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
              />
            </svg>
            <span>{t('genesisEagle')}</span>
          </NavLink>
        </NavContainer>
      </SidebarContainer>
    </>
  )
}
